// Chạy `sync --all` bằng BINARY MỚI sau khi `avatar update` cài xong (#211 phase-03).
//
// Tiến trình MỚI, không gọi `runSyncAllAcrossRegisteredWorkspaces` in-process: sau `npm i -g`
// code đang nằm trong RAM vẫn là bản CŨ ⇒ gọi in-process = sync bằng đúng bản người dùng vừa
// yêu cầu thay. Đường dẫn phải do `resolveAvatarBinaryPath` trả (PATH lookup theo TÊN), KHÔNG
// phải `process.argv[1]`.
import { spawn } from "node:child_process";

import { detectHostPlatform } from "./detect-host-platform.js";

/** Tên lệnh tra trên PATH. Đặt tên hằng để test và call site không gõ lệch chuỗi. */
export const AVATAR_BINARY_NAME = "avatar";

export interface ReexecSyncAllResult {
  /** exit code của tiến trình con; `null` khi con chết vì tín hiệu. */
  code: number | null;
  signal: NodeJS.Signals | null;
  /** Cha nhận SIGINT/SIGTERM khi con đang chạy — mã thoát đúng là 130, KHÔNG phải `code`. */
  interrupted: boolean;
  /** Không spawn được (ENOENT/EACCES). Con KHÔNG chạy ⇒ không có gì để propagate. */
  spawnError?: string;
}

/** Args của tiến trình con. Tách ra để unit test không phải spawn thật. */
export function buildSyncAllArgs(): string[] {
  return ["sync", "--all"];
}

// Binary npm global trên Windows là shim `.cmd` ⇒ spawn phải qua shell (cùng lý do
// `self-update-cli-via-npm-global-install.ts:18`). POSIX chạy thẳng, không shell — tránh
// đường quote hoá vô ích cho path có dấu cách.
function needsShell(): boolean {
  return detectHostPlatform() === "win32";
}

/**
 * Spawn `<binaryPath> sync --all`, chờ xong, trả mã thoát.
 *
 * `stdio: "inherit"` để người dùng thấy bảng tổng kết + prompt login của từng workspace.
 * Hệ quả BẮT BUỘC biết: con CÙNG PROCESS GROUP với cha ⇒ Ctrl-C bắn cả nhóm, cha chết ngay
 * nếu không tự bắt tín hiệu. Handler dưới đây chỉ ghi cờ (không exit) để cha sống đủ lâu mà
 * chờ con dọn dẹp rồi in cảnh báo — mặc định thì không dòng nào kịp chạy.
 */
export function reexecSyncAllWithBinary(binaryPath: string): Promise<ReexecSyncAllResult> {
  const shell = needsShell();
  const command = shell ? `"${binaryPath}"` : binaryPath;
  const child = spawn(command, buildSyncAllArgs(), { stdio: "inherit", shell });

  let interrupted = false;
  const onSignal = (): void => {
    interrupted = true;
  };
  process.on("SIGINT", onSignal);
  process.on("SIGTERM", onSignal);

  return new Promise<ReexecSyncAllResult>((resolve) => {
    const finish = (result: ReexecSyncAllResult): void => {
      process.off("SIGINT", onSignal);
      process.off("SIGTERM", onSignal);
      resolve(result);
    };
    // `error` (ENOENT khi binary vừa bị gỡ, EACCES khi mất quyền chạy) KHÔNG bao giờ kèm
    // `close` ⇒ phải tự resolve, nếu không `update` treo vĩnh viễn.
    child.once("error", (err: Error) =>
      finish({ code: null, signal: null, interrupted, spawnError: err.message }),
    );
    child.once("close", (code, signal) => finish({ code, signal, interrupted }));
  });
}
